import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronDown, User, Receipt, LogOut, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { signOut } from 'aws-amplify/auth';
import { useThemeMode } from '../../../../theme/ThemeProvider';
import { AppDispatch, RootState } from '../../../../store/store';
import { logout } from '../../../../store/reducers/authReducer';
import GradientAvatar from '../../../../components/GradientAvatar';
import { useRole } from '../../../../contexts/RoleContext/RoleContext';
import SignOutOverlay from '../../../../components/SignOutOverlay';

interface UserboxProps {
  isMinimal?: boolean;
  isStandalone?: boolean;
}

function HeaderUserbox({ isMinimal = false, isStandalone = false }: UserboxProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const { isDark } = useThemeMode();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.auth?.user);
  const profile = useSelector((state: RootState) => state.auth?.profile);
  const pk = useSelector((state: RootState) => state.auth?.pk);
  const { role } = useRole();
  const isAdmin = role === 'admin';

  const NAV_LINKS = [
    { label: 'My Profile', icon: User, path: `/patient-details/PersonalInformation?patientId=${pk}` },
    { label: 'Transactions', icon: Receipt, path: '/transactions' },
  ];
  const isNurse = role === 'nurse';
  const isDoctor = role === 'doctor';

  const fullName = isAdmin
    ? (profile?.AdminName || user?.username || 'Admin')
    : isDoctor
      ? (profile?.HealthProviderName
        || profile?.DoctorName
        || [profile?.FirstName, profile?.LastName].filter(Boolean).join(' ')
        || user?.username
        || 'Doctor')
      : isNurse
        ? (profile?.NurseName
          || [profile?.FirstName, profile?.LastName].filter(Boolean).join(' ')
          || user?.username
          || 'Coordinator')
        : (() => {
          const patientName = profile?.PatientName || profile?.name || user?.username || 'User';
          const fn = profile?.FirstName || patientName.split(' ')[0] || 'User';
          const ln = profile?.LastName || (profile?.PatientName?.split(' ').slice(1).join(' ')) || '';
          return fn && ln ? `${fn} ${ln}`.trim() : fn || patientName;
        })();
  const firstName = isAdmin
    ? (profile?.AdminName?.split(' ')[0] || 'Admin')
    : isDoctor
      ? (profile?.HealthProviderName?.split(' ')[0]
        || profile?.DoctorName?.split(' ')[0]
        || profile?.FirstName
        || fullName.split(' ')[0]
        || 'Doctor')
      : isNurse
        ? (profile?.NurseName?.split(' ')[0]
          || profile?.FirstName
          || fullName.split(' ')[0]
          || 'Coordinator')
        : (profile?.FirstName || fullName.split(' ')[0] || 'User');
  const email = profile?.Email || '';

  const handleClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) handleClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  const handleSignOut = async () => {
    handleClose();
    setIsSigningOut(true);
    try {
      await signOut();
      localStorage.removeItem('stepper_completed');
      dispatch(logout());
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      setIsSigningOut(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={() => setOpen(!isOpen)}
        className={[
          'flex items-center gap-2 px-1 sm:px-2 py-1.5 rounded-xl',
          'transition-all duration-200',
          isDark
            ? 'hover:bg-white/[0.07] border border-transparent hover:border-white/[0.08]'
            : 'hover:bg-slate-50 border border-transparent hover:border-slate-200/80',
          isOpen && (isDark
            ? '!bg-white/[0.08] !border-white/[0.10] ring-1 ring-white/[0.08]'
            : '!bg-slate-50 ring-1 ring-slate-200/70'),
        ].join(' ')}
        aria-label="User menu"
      >
        {/* Avatar with online dot */}
        <div className="relative w-8 h-8 flex-shrink-0">
          {/* Avatar — no overflow-hidden so dot isn't clipped */}
          <div className="w-8 h-8 rounded-[10px] overflow-hidden">
            <GradientAvatar name={fullName} size={32} />
          </div>
          {/* Online dot — outside overflow-hidden, always visible */}
          <span
            className={[
              'absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-[2px]',
              'shadow-dark-online',
              isDark ? 'ring-dark-chrome-header' : 'ring-white',
            ].join(' ')}
          />
        </div>

        {/* Name (desktop) */}
        {!isMinimal && (
          <span className={[
            'hidden lg:block text-caption font-semibold max-w-[100px] truncate',
            "text-slate-700 dark:text-slate-200",
          ].join(' ')}>
            {firstName}
          </span>
        )}

        <ChevronDown
          size={13}
          strokeWidth={2.2}
          className={[
            // Phones show the avatar alone: the header row has no room to spare.
            'hidden sm:block flex-shrink-0 transition-all duration-200',
            "text-slate-400 dark:text-slate-300",
            isOpen ? 'rotate-180' : 'rotate-0',
          ].join(' ')}
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={handleClose} />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              className={[
                'absolute right-0 mt-2.5 z-50 w-[268px] overflow-hidden rounded-2xl',
                isDark
                  ? 'bg-dark-chrome-dropdown shadow-dark-dropdown border border-dark-border'
                  : 'bg-light-surface-panel shadow-light-dropdown border border-light-border',
              ].join(' ')}
              style={{
                backdropFilter: 'blur(40px) saturate(1.6)',
                WebkitBackdropFilter: 'blur(40px) saturate(1.6)',
              }}
            >
              {/* Animated brand bar */}
              <div
                className="h-[2.5px] bg-gradient-to-r from-brand-700 via-brand-500 to-brand-300 bg-[length:200%_100%] animate-shimmer"
              />

              {/* Profile header */}
              <div className={[
                'flex items-center gap-3.5 px-4 py-4',
                'border-b',
                isDark ? 'border-white/[0.05]' : 'border-slate-100',
              ].join(' ')}>
                {/* Large avatar — online dot sits OUTSIDE overflow-hidden */}
                <div className="relative flex-shrink-0">
                  <div className="w-11 h-11 rounded-2xl overflow-hidden">
                    <GradientAvatar name={fullName} size={44} />
                  </div>
                  {/* Online dot outside the overflow-hidden wrapper */}
                  <span
                    className={[
                      'absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 ring-[2px] shadow-dark-online-lg',
                      isDark ? 'ring-dark-chrome-dropdown' : 'ring-white',
                    ].join(' ')}
                  />
                </div>

                {/* User info */}
                <div className="flex-1 min-w-0">
                  <p className={[
                    'text-body-xs font-bold truncate',
                    "text-slate-800 dark:text-white",
                  ].join(' ')}>
                    {fullName || 'Account'}
                  </p>
                  {email && (
                    <p className={[
                      'text-caption-sm truncate mt-0.5',
                      "text-slate-400 dark:text-slate-400",
                    ].join(' ')}>
                      {email}
                    </p>
                  )}
                </div>
              </div>

              {/* Nav links */}
              {role === 'patient' && !isMinimal && !isStandalone && (
                <div className="py-1.5">
                  {NAV_LINKS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.label}
                        onClick={() => { handleClose(); navigate(item.path); }}
                        className={[
                          'w-full flex items-center gap-3 px-4 py-2.5 text-left',
                          'transition-all duration-150 group',
                          isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-slate-50',
                        ].join(' ')}
                      >
                        <div
                          className={[
                            'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0',
                            isDark ? 'bg-dark-icon-bg' : 'bg-slate-100',
                          ].join(' ')}
                        >
                          <Icon
                            size={15}
                            strokeWidth={2}
                            className={"text-slate-500 dark:text-slate-400"}
                          />
                        </div>
                        <span className={[
                          'flex-1 text-[13px] font-medium tracking-[-0.01em]',
                          "text-slate-700 dark:text-slate-300",
                        ].join(' ')}>
                          {item.label}
                        </span>
                        <ChevronRight
                          size={13}
                          className={[
                            'opacity-0 group-hover:opacity-60 transition-all duration-150 group-hover:translate-x-0.5',
                            "text-slate-400 dark:text-slate-300",
                          ].join(' ')}
                        />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Sign out */}
              <div className={[
                'px-3 py-3 border-t',
                isDark ? 'border-dark-border-subtle bg-dark-hover-subtle' : 'border-slate-100 bg-slate-50/40',
              ].join(' ')}>
                <button
                  onClick={handleSignOut}
                  className={[
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl',
                    'transition-all duration-150 group',
                    isDark
                      ? 'text-red-400 hover:text-red-300 hover:bg-red-400/[0.08]'
                      : 'text-red-500 hover:text-red-600 hover:bg-red-50',
                  ].join(' ')}
                >
                  <div className={[
                    'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0',
                    isDark ? 'bg-red-400/[0.08]' : 'bg-red-50',
                  ].join(' ')}>
                    <LogOut size={13} strokeWidth={2} />
                  </div>
                  <span className="text-caption font-semibold">Sign out</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isSigningOut && <SignOutOverlay />}
      </AnimatePresence>
    </div>
  );
}

export default HeaderUserbox;
